import { c as _c } from "react/compiler-runtime";
// Общие детали разделов «Мои покупки» / «Возвраты» / «Отзывы» кабинета
// (макет «Профиль»): миниатюры с подписью, счётчик количества, оранжевый
// чекбокс, лента медиа, форматирование дат и оболочка модалки 1:1.

import { Dialog, DialogContent, DialogTitle } from "@osmix/ui/dialog";
import { Play } from "lucide-react";
import { CheckIcon, CloseIcon, MinusOutlineIcon, PlusOutlineIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
const MONTHS_GEN = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];

/** «18.03.26» — дата статуса позиции. */
export const fmtShortDate = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)}`;
};

/** «14 мая» — дата заявки. */
export const fmtDayMonth = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS_GEN[d.getMonth()]}`;
};

/** «2 декабря 2025» — дата отзыва. */
export const fmtLongDate = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS_GEN[d.getMonth()]} ${d.getFullYear()}`;
};
const DAYS_GEN = ["", "одного", "двух", "трёх", "четырёх", "пяти", "шести", "семи", "восьми", "девяти", "десяти"];

/** Рабочие дни между подачей и сроком рассмотрения — для фразы плашки. */
export const reviewDaysText = (createdAt: string, dueAt?: string | null) => {
  if (!dueAt) return null;
  // Сравниваем календарные дни: срок и дата подачи пишутся в разные моменты
  // одной транзакции, миллисекунды не должны давать лишний день.
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const to = day(new Date(dueAt));
  let n = 0;
  const d = new Date(day(new Date(createdAt)));
  while (d.getTime() < to && n < 60) {
    d.setDate(d.getDate() + 1);
    const w = d.getDay();
    if (w !== 0 && w !== 6) n += 1;
  }
  if (n <= 0) return null;
  const word = DAYS_GEN[n];
  const unit = n % 10 === 1 && n % 100 !== 11 ? "рабочего дня" : "рабочих дней";
  return `${n}${word ? ` (${word})` : ""} ${unit}`;
};

/** Миниатюра товара 50×66 r4 с подписью 12/16 (размер / «2 шт.») на белой вуали 36 % справа снизу. */
export function Thumb(t0) {
  const $ = _c(14);
  const {
    src,
    label,
    className,
    w: t1,
    h: t2
  } = t0;
  const w = t1 === undefined ? 50 : t1;
  const h = t2 === undefined ? 66 : t2;
  let t3;
  if ($[0] !== className) {
    t3 = cn("relative block shrink-0 overflow-hidden rounded-[4px] bg-white", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== h || $[3] !== w) {
    t4 = {
      width: w,
      height: h
    };
    $[2] = h;
    $[3] = w;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== src) {
    t5 = src ? <img src={src} alt="" className="size-full object-cover" loading="lazy" /> : null;
    $[5] = src;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== label) {
    t6 = label ? <span className="absolute right-0 bottom-0 max-w-full truncate rounded-tl-[2px] bg-white/36 px-0.5 text-[12px] leading-4 text-[#1F1F1F] backdrop-blur-[4px]">{label}</span> : null;
    $[7] = label;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = <span className={t3} style={t4}>{t5}{t6}</span>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}

/** Фото товара на белой подложке 100×133 r16 (строки покупок/отзывов/возвратов). */
export function ProductTile(t0) {
  const $ = _c(12);
  const {
    src,
    className,
    children,
    w: t1,
    h: t2
  } = t0;
  const w = t1 === undefined ? 100 : t1;
  const h = t2 === undefined ? 133 : t2;
  let t3;
  if ($[0] !== className) {
    t3 = cn("relative block shrink-0 overflow-hidden rounded-[16px] bg-white", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== h || $[3] !== w) {
    t4 = {
      width: w,
      height: h
    };
    $[2] = h;
    $[3] = w;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== src) {
    t5 = src ? <img src={src} alt="" className="size-full object-cover" loading="lazy" /> : null;
    $[5] = src;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== children || $[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <span className={t3} style={t4}>{t5}{children}</span>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

/** Оранжевый чекбокс 20×20 r8 #FF5E00 (выбор позиции заявки). */
export function OrangeCheck(t0) {
  const $ = _c(15);
  const {
    checked,
    onChange,
    disabled,
    label
  } = t0;
  let t1;
  if ($[0] !== checked || $[1] !== onChange) {
    t1 = () => onChange?.(!checked);
    $[0] = checked;
    $[1] = onChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = checked ? "bg-[#FF5E00] text-white" : "border border-[#1F1F1F]/24 bg-white";
  const t3 = disabled && "cursor-default";
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = cn("grid size-5 shrink-0 place-items-center rounded-[8px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ds-focus", t2, t3);
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== checked) {
    t5 = checked ? <CheckIcon size={16} /> : null;
    $[6] = checked;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== checked || $[9] !== disabled || $[10] !== label || $[11] !== t1 || $[12] !== t4 || $[13] !== t5) {
    t6 = <button type="button" role="checkbox" aria-checked={checked} aria-label={label} disabled={disabled} onClick={t1} className={t4}>{t5}</button>;
    $[8] = checked;
    $[9] = disabled;
    $[10] = label;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

/** Счётчик количества #ECECEC r8 h30 (− N +). */
export function QtyStepper(t0) {
  const $ = _c(25);
  const {
    value,
    min: t1,
    max,
    onChange,
    className
  } = t0;
  const min = t1 === undefined ? 1 : t1;
  const readOnly = !onChange;
  let t2;
  if ($[0] !== className) {
    t2 = cn("inline-flex h-[30px] items-center gap-2.5 rounded-[8px] bg-[#ECECEC] px-2", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = readOnly || value <= min;
  let t4;
  if ($[2] !== min || $[3] !== onChange || $[4] !== value) {
    t4 = () => onChange?.(Math.max(min, value - 1));
    $[2] = min;
    $[3] = onChange;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <MinusOutlineIcon size={20} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t4) {
    t6 = <button type="button" aria-label={"\u041C\u0435\u043D\u044C\u0448\u0435"} disabled={t3} onClick={t4} className="grid size-5 place-items-center text-[#1F1F1F] outline-none disabled:text-[#1F1F1F]/24">{t5}</button>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== value) {
    t7 = <span className="min-w-2.5 text-center text-[16px] leading-[22px] font-bold tracking-[-0.32px] text-[#161616] tabular-nums">{value}</span>;
    $[10] = value;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = readOnly || value >= max;
  let t9;
  if ($[12] !== max || $[13] !== onChange || $[14] !== value) {
    t9 = () => onChange?.(Math.min(max, value + 1));
    $[12] = max;
    $[13] = onChange;
    $[14] = value;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <PlusOutlineIcon size={20} />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t8 || $[18] !== t9) {
    t11 = <button type="button" aria-label={"\u0411\u043E\u043B\u044C\u0448\u0435"} disabled={t8} onClick={t9} className="grid size-5 place-items-center text-[#1F1F1F] outline-none disabled:text-[#1F1F1F]/24">{t10}</button>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t2 || $[22] !== t6 || $[23] !== t7) {
    t12 = <span className={t2}>{t6}{t7}{t11}</span>;
    $[20] = t11;
    $[21] = t2;
    $[22] = t6;
    $[23] = t7;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}

/** Лента медиа 40×53 r4 с зазором 4; видео — белый ▶ 50 %. */
export function MediaStrip(t0) {
  const $ = _c(12);
  const {
    items,
    w: t1,
    h: t2,
    className
  } = t0;
  const w = t1 === undefined ? 40 : t1;
  const h = t2 === undefined ? 53 : t2;
  if (items.length === 0) {
    return null;
  }
  let t3;
  if ($[0] !== className) {
    t3 = cn("flex gap-1 overflow-x-auto [scrollbar-width:none]", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== h || $[3] !== items || $[4] !== w) {
    let t5;
    if ($[6] !== h || $[7] !== w) {
      t5 = (m, i) => <a key={`${m.url}-${i}`} href={m.url} target="_blank" rel="noreferrer" className="relative block shrink-0 overflow-hidden rounded-[4px] bg-[#EDEDED] outline-none focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40" style={{
        width: w,
        height: h
      }}>{m.kind === "video" ? <video src={m.url} muted={true} playsInline={true} preload="metadata" className="size-full object-cover" /> : <img src={m.thumbnailUrl || m.url} alt="" loading="lazy" className="size-full object-cover" />}{m.kind === "video" ? <Play className="absolute top-1/2 left-1/2 size-[18px] -translate-x-1/2 -translate-y-1/2 fill-white/50 text-transparent" /> : null}</a>;
      $[6] = h;
      $[7] = w;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = items.map(t5);
    $[2] = h;
    $[3] = items;
    $[4] = w;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/** Модалка кабинета: белая r20, затемнение чёрное 20 % + блюр, свой крестик 24. */
export function CabinetModal(t0) {
  const $ = _c(26);
  const {
    open,
    onOpenChange,
    title,
    width: t1,
    children,
    className,
    titleClassName
  } = t0;
  const width = t1 === undefined ? 440 : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("gap-0 rounded-[20px] border-0 bg-white p-4 shadow-none sm:max-w-none", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `min(${width}px, calc(100vw - 32px))`;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      width: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== titleClassName) {
    t5 = cn("pt-1 text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-ds-ink", titleClassName);
    $[4] = titleClassName;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5 || $[7] !== title) {
    t6 = <DialogTitle className={t5}>{title}</DialogTitle>;
    $[6] = t5;
    $[7] = title;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== onOpenChange) {
    t7 = () => onOpenChange(false);
    $[9] = onOpenChange;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <CloseIcon size={24} />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7) {
    t9 = <button type="button" aria-label={"\u0417\u0430\u043A\u0440\u044B\u0442\u044C"} onClick={t7} className="grid size-8 shrink-0 place-items-center rounded-[8px] text-[#333] outline-none hover:bg-ds-quaternary focus-visible:ring-2 focus-visible:ring-ds-focus">{t8}</button>;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t6 || $[15] !== t9) {
    t10 = <div className="mb-4 flex items-start justify-between gap-3">{t6}{t9}</div>;
    $[14] = t6;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== children || $[18] !== t10 || $[19] !== t2 || $[20] !== t4) {
    t11 = <DialogContent showCloseButton={false} overlayClassName="bg-black/20 backdrop-blur-[2px]" className={t2} style={t4}>{t10}{children}</DialogContent>;
    $[17] = children;
    $[18] = t10;
    $[19] = t2;
    $[20] = t4;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== onOpenChange || $[23] !== open || $[24] !== t11) {
    t12 = <Dialog open={open} onOpenChange={onOpenChange}>{t11}</Dialog>;
    $[22] = onOpenChange;
    $[23] = open;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}

/** Серая кнопка кабинета (#989AA2 12 %), высота по месту. */
export const grayButton = "inline-flex items-center justify-center gap-2 bg-[#989AA2]/12 font-semibold text-[#1F1F1F] outline-none transition-colors hover:bg-[#989AA2]/20 focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40 disabled:cursor-not-allowed disabled:text-[#1F1F1F]/30 disabled:hover:bg-[#989AA2]/12";

/** Пустое состояние раздела на белой карточке. */
export function CabinetEmpty(t0) {
  const $ = _c(10);
  const {
    title,
    hint,
    action
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <p className="text-[16px] font-medium text-[#1F1F1F]">{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hint) {
    t2 = hint ? <p className="max-w-sm text-[14px] text-[#1F1F1F]/48">{hint}</p> : null;
    $[2] = hint;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== action) {
    t3 = action ? <div className="mt-2">{action}</div> : null;
    $[4] = action;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-[20px] bg-white px-6 py-14 text-center">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}